import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import type { PortalContext as PortalContextDto } from '@imsenta/shared';
import { api } from './api';

/**
 * Whether this browser is looking at a tenant catalog portal.
 *
 * The server decides, from the hostname the request arrived on. The client only
 * asks — it never infers tenancy from the URL itself, because that would be a
 * second, weaker source of truth for something the session already settles.
 */
interface PortalState {
  loading: boolean;
  context: PortalContextDto | null;
  isPortal: boolean;
  organisation: string | null;
  branding: PortalContextDto extends { portal: infer P } ? (P extends { branding: infer B } ? B | null : null) : null;
}

const PortalStateContext = createContext<PortalState>({
  loading: true,
  context: null,
  isPortal: false,
  organisation: null,
  branding: null,
});

export function PortalProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ['portal-context'],
    queryFn: () => api.get<PortalContextDto>('/portal/context'),
    // The hostname cannot change without a page load, so this never goes stale.
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });

  const value = useMemo<PortalState>(
    () => ({
      loading: isLoading,
      context: data ?? null,
      isPortal: Boolean(data?.portal),
      organisation: data?.portal?.organisation ?? null,
      branding: (data?.portal?.branding ?? null) as PortalState['branding'],
    }),
    [data, isLoading],
  );

  // Apply the organisation's accent colour and page title to the whole portal.
  useEffect(() => {
    if (!data?.portal) return;
    const { branding, organisation } = data.portal;
    document.documentElement.style.setProperty('--portal-accent', branding.accentColor);
    document.title = branding.title || `${organisation} policy library`;
  }, [data]);

  return <PortalStateContext.Provider value={value}>{children}</PortalStateContext.Provider>;
}

export function usePortal(): PortalState {
  return useContext(PortalStateContext);
}
